/**
 * Gossip about the key log head — the thing that stands in for an auditor.
 *
 * The chain check (keyLog.mjs) catches a REWRITTEN history. It cannot catch a
 * server that keeps two histories and shows each person their own: every
 * chain verifies perfectly alone. Signal rules that out with an independent
 * auditor; we have none — but we have something they designed auditors
 * around not having: every pair of clients already shares an E2EE channel.
 *
 * So each message envelope carries the sender's view of the log head —
 * `{s: seq, h: head}`, sealed inside the ratchet where the server can
 * neither read it nor strip it without destroying the message. The receiver
 * compares it against the head ITS chain produced at that seq. Two views of
 * one log agree everywhere; two logs disagree at every entry after the
 * fork. One conversation is enough to catch it, and both sides catch it,
 * because gossip flows both ways.
 *
 * A mismatch is a split view and it is STICKY, like every key-log alarm:
 * the server that kept two logs answers the next request too.
 */
import { ALARM_KEY } from './keyLogWatch.mjs';

const HEADS_KEY = 'keylog:heads';            // { heads: { seq: head } }
const PENDING_KEY = 'keylog:gossip-pending'; // { seq: { head, from, at } }

/** Heads are one per device event; 512 covers years of this log. */
export const MAX_HEADS = 512;

export const SPLIT_VIEW = 'split-view';

/** What we put into an outgoing envelope. Null while the log is unchecked. */
export function currentGossip(state) {
  return state && state.seq > 0 && state.head ? { s: state.seq, h: state.head } : null;
}

/** Remember the head our own verified chain produced at every seq. */
export async function recordOwnHeads(store, added, state) {
  const held = (await store.getValue(HEADS_KEY)) || { heads: {} };
  for (const e of added || []) if (e && e.seq && e.head) held.heads[e.seq] = e.head;
  if (state && state.seq > 0 && state.head) held.heads[state.seq] = state.head;
  const seqs = Object.keys(held.heads).map(Number).sort((a, b) => a - b);
  while (seqs.length > MAX_HEADS) delete held.heads[seqs.shift()];
  await store.setValue(HEADS_KEY, held);
}

async function raiseSplit(store, { at, from, ourHead, theirHead }) {
  const alarm = {
    error: SPLIT_VIEW, at, from,
    ourHead, theirHead,
    seenAt: new Date().toISOString(),
  };
  await store.setValue(ALARM_KEY, alarm);
  return alarm;
}

/**
 * Compare a peer's claimed head with our own chain.
 *
 * 'match'   — same log, as it should be.
 * 'split'   — the server showed us different histories. Alarm raised.
 * 'ahead'   — the peer has seen further than we have; held and re-checked
 *             after our next chain walk. Two peers claiming DIFFERENT heads
 *             for the same future seq is already a split — no need to wait.
 * 'unknown' — older than anything we recorded (we joined later); honest
 *             "cannot compare", not a pass.
 */
export async function checkPeerHead(store, { seq, head, from }) {
  const s = Number(seq);
  if (!s || s < 1 || typeof head !== 'string' || !head) return { verdict: 'malformed' };
  const held = (await store.getValue(HEADS_KEY)) || { heads: {} };
  const ours = held.heads[s];
  if (ours) {
    if (ours === head) return { verdict: 'match' };
    return { verdict: 'split', alarm: await raiseSplit(store, { at: s, from, ourHead: ours, theirHead: head }) };
  }
  const maxSeq = Math.max(0, ...Object.keys(held.heads).map(Number));
  if (s > maxSeq) {
    const pending = (await store.getValue(PENDING_KEY)) || {};
    const prior = pending[s];
    if (prior && prior.head !== head) {
      // Two claims for the same seq that cannot both be true. Whichever of
      // the three parties is being lied to, there are two logs somewhere.
      return { verdict: 'split', alarm: await raiseSplit(store, { at: s, from, ourHead: prior.head, theirHead: head }) };
    }
    if (!prior) {
      pending[s] = { head, from, at: new Date().toISOString() };
      await store.setValue(PENDING_KEY, pending);
    }
    return { verdict: 'ahead' };
  }
  return { verdict: 'unknown' };
}

/**
 * After our own chain advances: every held claim now inside our range must
 * match the head we computed ourselves. Returns the alarm if one was raised.
 */
export async function resolvePendingGossip(store) {
  const pending = (await store.getValue(PENDING_KEY)) || {};
  const held = (await store.getValue(HEADS_KEY)) || { heads: {} };
  let alarm = null;
  const rest = {};
  for (const [seqStr, p] of Object.entries(pending)) {
    const ours = held.heads[Number(seqStr)];
    if (!ours) { rest[seqStr] = p; continue; }
    if (ours !== p.head) {
      alarm = await raiseSplit(store, { at: Number(seqStr), from: p.from, ourHead: ours, theirHead: p.head });
    }
  }
  await store.setValue(PENDING_KEY, rest);
  return alarm;
}

export { HEADS_KEY, PENDING_KEY };
